'use client';
// TODO: Evaluate import 'client only'
import React, { useEffect } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    dataLayer?: Object[];
    [key: string]: any;
  }
}

type JSONValue = string | number | boolean | JSONValue[] | { [key: string]: JSONValue };

export type GTMParams = {
  gtmId: string;
  gtmScriptUrl?: string;
  dataLayer?: { [key: string]: JSONValue };
  dataLayerName?: string;
  auth?: string;
  preview?: string;
  nonce?: string;
};

export type GAParams = {
  gaId: string;
  dataLayerName?: string;
  debugMode?: boolean;
  nonce?: string;
};

export type GoogleMapsEmbed = {
  height?: number | string;
  width?: number | string;
  mode: 'place' | 'view' | 'directions' | 'streetview' | 'search';
  apiKey: string;
  style?: string;
  allowfullscreen?: boolean;
  loading?: 'eager' | 'lazy';
  q?: string;
  id?: string;
  center?: string;
  zoom?: string;
  maptype?: string;
  language?: string;
  region?: string;
};

export type YouTubeEmbed = {
  height?: number;
  width?: number;
  videoid: string;
  playlabel?: string;
  params?: string;
  style?: string;
};

let currDataLayerName = 'dataLayer';

export function GoogleTagManager(props: GTMParams) {
  const {
    gtmId,
    gtmScriptUrl = 'https://www.googletagmanager.com/gtm.js',
    dataLayerName = 'dataLayer',
    auth,
    preview,
    dataLayer,
    nonce
  } = props;

  currDataLayerName = dataLayerName;

  const gtmLayer = dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : '';
  const gtmAuth = auth ? `&gtm_auth=${auth}` : '';
  const gtmPreview = preview ? `&gtm_preview=${preview}&gtm_cookies_win=x` : '';

  useEffect(() => {
    // performance.mark is being used as a feature use signal. While it is traditionally used for performance
    // benchmarking it is low overhead and thus considered safe to use in production and it is a widely available
    // existing API.
    // The performance measurement will be handled by Chrome Aurora

    performance.mark('mark_feature_usage', {
      detail: {
        feature: 'next-third-parties-gtm'
      }
    });
  }, []);

  return (
    <>
      <Script
        id="_next-gtm-init"
        dangerouslySetInnerHTML={{
          __html: `
      (function(w,l){
        w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
        ${dataLayer ? `w[l].push(${JSON.stringify(dataLayer)})` : ''}
      })(window,'${dataLayerName}');`
        }}
        nonce={nonce}
        strategy="beforeInteractive"
        defer={false}
      />
      <Script
        id="_next-gtm"
        data-ntpc="GTM"
        src={`${gtmScriptUrl}?id=${gtmId}${gtmLayer}${gtmAuth}${gtmPreview}`}
        nonce={nonce}
        defer={false}
        strategy="beforeInteractive"
      />
    </>
  );
}

export const sendGTMEvent = (data: Object, dataLayerName?: string) => {
  // special case if we are sending events before GTM init and we have custom dataLayerName
  const dataLayer = dataLayerName || currDataLayerName;
  // define dataLayer so we can still queue up events before GTM init
  window[dataLayer] = window[dataLayer] || [];
  window[dataLayer].push(data);
};
